<script setup lang="ts">
import { computed, useId } from 'vue'
import { useTooltipPosition } from '@/composables/useTooltipPosition'

interface Props {
  text: string
}

defineProps<Props>()

const tooltipId = `tooltip-${useId()}`
const { isVisible, tooltipRef, triggerRef, tooltipStyle, showTooltip, hideTooltip } =
  useTooltipPosition()

const tooltipClasses = computed(() => ({
  'tooltip__content': true,
  'tooltip__content--visible': isVisible.value,
}))
</script>

<template>
  <slot v-if="!text" />
  <span
    v-else
    ref="triggerRef"
    class="tooltip"
    tabindex="0"
    :aria-describedby="isVisible ? tooltipId : undefined"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
    @focus="showTooltip"
    @blur="hideTooltip"
  >
    <slot />
    <Teleport to="body">
      <div
        ref="tooltipRef"
        :id="tooltipId"
        role="tooltip"
        :class="tooltipClasses"
        :style="tooltipStyle"
      >
        {{ text }}
      </div>
    </Teleport>
  </span>
</template>

<style scoped>
.tooltip {
  display: inline-flex;
  align-items: center;
  outline: none;
}

.tooltip:focus-visible {
  box-shadow: var(--shadow-focus);
  border-radius: var(--radius-sm);
}
</style>

<style>
.tooltip__content {
  position: fixed;
  z-index: var(--z-max);
  max-width: 320px;
  padding: var(--spacing-xs) var(--spacing-sm);
  background-color: var(--color-background-content);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--color-text-primary);
  opacity: 0;
  visibility: hidden;
  transition:
    opacity var(--transition-duration-fast) var(--transition-easing-ease-out),
    visibility var(--transition-duration-fast) var(--transition-easing-ease-out);
  pointer-events: none;
  white-space: normal;
}

.tooltip__content--visible {
  opacity: 1;
  visibility: visible;
}
</style>
